import {Component, Input, OnInit} from '@angular/core';
import {FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {InputText} from 'primeng/inputtext';
import {Password} from 'primeng/password';
import {Select} from 'primeng/select';
import {FloatLabel} from 'primeng/floatlabel';
import {ToggleButton} from 'primeng/togglebutton';
import {MAILBOX_PROVIDERS_OPTIONS, MailboxProvider} from '../../mailbox.model';

@Component({
	selector: 'app-inbound-form',
	standalone: true,
	imports: [
		ReactiveFormsModule,
		InputText,
		Password,
		Select,
		FloatLabel,
		ToggleButton
	],
	template: `
		<form [formGroup]="form" class="p-fluid">
			<div class="flex flex-column gap-3">
				<!-- PROVIDER -->
				<div class="flex flex-column">
					<p-floatlabel variant="on">
						<p-select [options]="MAILBOX_PROVIDERS_OPTIONS"
								  formControlName="client"
								  optionLabel="label"
								  optionValue="value"
								  fluid/>
						<label for="client">Provider</label>
					</p-floatlabel>
					<small class="text-500">Seleziona il provider della casella (es. Gmail, Aruba)</small>
				</div>

				<!-- EMAIL -->
				<div class="flex flex-column">
					<p-floatlabel variant="on">
						<input pInputText formControlName="email" fluid/>
						<label for="email">Email</label>
					</p-floatlabel>
					<small class="text-500">Indirizzo email completo (es. nome&#64;gmail.com)</small>
				</div>

				<!-- USERNAME -->
				<div class="flex flex-column">
					<p-floatlabel variant="on">
						<input pInputText
							   formControlName="username"
							   autocomplete="off"
							   fluid/>
						<label for="username">Username</label>
					</p-floatlabel>
					<small class="text-500">Spesso coincide con l’email</small>
				</div>

				<!-- PASSWORD -->
				<div class="flex flex-column">
					<p-floatlabel variant="on">
						<p-password formControlName="password"
									[toggleMask]="true"
									[feedback]="false"
									autocomplete="new-password"
									fluid />
						<label for="password">Password</label>
					</p-floatlabel>
					<small class="text-500">Password della casella o password applicativa</small>
				</div>

				<!-- HOST -->
				@if (isHostVisible()) {
					<div class="flex flex-column">
						<p-floatlabel variant="on">
							<input pInputText formControlName="host" fluid/>
							<label for="host">Host</label>
						</p-floatlabel>
						<small class="text-500">Server IMAP (es. imap.gmail.com)</small>
					</div>
				}

				<!-- CUSTOM -->
				@if (isCustom()) {
					<div class="flex flex-column">
						<p-floatlabel variant="on">
							<input type="number" pInputText formControlName="imap_port" fluid/>
							<label for="imap_port">IMAP Port</label>
						</p-floatlabel>
						<small class="text-500">Porta IMAP (es. 993 con SSL)</small>
					</div>

					<div class="flex flex-column">
						<p-floatlabel variant="on">
							<input pInputText formControlName="protocol" fluid/>
							<label for="protocol">Protocol</label>
						</p-floatlabel>
						<small class="text-500">Protocollo di accesso (es. IMAP)</small>
					</div>

					<div class="flex flex-column">
						<p-toggleButton
							formControlName="ssl_enabled"
							onIcon="pi pi-check"
							offIcon="pi pi-times"
							onLabel="SSL"
							offLabel="SSL"/>
						<small class="text-500">Connessione sicura consigliata</small>
					</div>

					<div class="flex flex-column">
						<p-toggleButton
							formControlName="start_tls_enabled"
							onIcon="pi pi-check"
							offIcon="pi pi-times"
							onLabel="StartTLS"
							offLabel="StartTLS"/>
						<small class="text-500">Alternativa a SSL per connessione sicura</small>
					</div>
				}

			</div>
		</form>
	`
})
export class InboundFormComponent implements OnInit {
	@Input({ required: true }) form!: FormGroup;

	protected readonly MAILBOX_PROVIDERS_OPTIONS = MAILBOX_PROVIDERS_OPTIONS;

	client: MailboxProvider | null = null;

	ngOnInit() {
		this.client = this.form.get('client')?.value;

		this.form.get('client')?.valueChanges.subscribe(client => {
			this.client = client as MailboxProvider;
			this.applyValidation(this.client);
		});
	}

	isHostVisible() {
		return this.client === 'CUSTOM' || this.client === 'AWS_WORKMAIL';
	}

	isCustom() {
		return this.client === 'CUSTOM';
	}

	private applyValidation(provider: MailboxProvider | null) {
		const host     = this.form.get('host');
		const port     = this.form.get('imap_port');
		const protocol = this.form.get('protocol');

		host?.clearValidators();
		port?.clearValidators();
		protocol?.clearValidators();

		if (provider === 'AWS_WORKMAIL') {
			host?.setValidators([Validators.required]);
		}

		if (provider === 'CUSTOM') {
			host?.setValidators([Validators.required]);
			port?.setValidators([Validators.required]);
			protocol?.setValidators([Validators.required]);
		}

		host?.updateValueAndValidity();
		port?.updateValueAndValidity();
		protocol?.updateValueAndValidity();
	}
}
